import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notifications } from '@mantine/notifications';
import { Modal, Button, ControlledInput } from '@shared/ui';
import { useUpdateFlowerType, useCreateFlowerType, type FlowerType } from '@modules/products';

const flowerTypeSchema = yup.object({
    name: yup.string().trim().required('Название обязательно'),
});

type FlowerTypeForm = yup.InferType<typeof flowerTypeSchema>;

interface EditFlowerTypeModalProps {
    isOpen: boolean;
    onClose: () => void;
    flowerType: FlowerType | null;
    onSuccess?: () => void;
}

export default function EditFlowerTypeModal({ isOpen, onClose, flowerType, onSuccess }: EditFlowerTypeModalProps) {
    const updateFlowerType = useUpdateFlowerType();
    const createFlowerType = useCreateFlowerType();
    const isEdit = !!flowerType;
    const { control, handleSubmit, reset, formState: { errors } } = useForm<FlowerTypeForm>({
        resolver: yupResolver(flowerTypeSchema),
        defaultValues: { name: '' },
    });

    useEffect(() => {
        reset({ name: flowerType?.name ?? '' });
    }, [flowerType, reset]);

    const onSubmit = async (data: FlowerTypeForm) => {
        try {
            if (isEdit && flowerType) {
                await updateFlowerType.mutateAsync({
                    id: flowerType.id,
                    data: { name: data.name },
                });
            } else {
                await createFlowerType.mutateAsync({ name: data.name });
            }

            notifications.show({
                title: 'Успешно',
                message: `Тип цветка успешно ${isEdit ? 'обновлен' : 'создан'}`,
                color: 'green',
            });

            onSuccess?.();
            onClose();
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: `Ошибка при ${isEdit ? 'обновлении' : 'создании'} типа цветка`,
                color: 'red',
            });
        }
    };

    const handleClose = () => {
        reset({ name: '' });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} size={400} onClose={handleClose} title={isEdit ? "Редактировать тип цветка" : "Добавить тип цветка"} centered>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <ControlledInput
                        id="flower-type-name"
                        label="Название"
                        name="name"
                        control={control}
                        error={errors.name}
                    />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <Button type="button" onClick={handleClose} variant='secondary'>
                            Отмена
                        </Button>
                        <Button type="submit" variant="primary" loading={updateFlowerType.isPending || createFlowerType.isPending}>
                            Сохранить
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}

